import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatError } from "@/lib/api";
import { Users, Building2, UserCircle, Briefcase, ClipboardList } from "lucide-react";
import SchoolSetupCard from "@/components/SchoolSetupCard";
import TeacherDashboard from "@/pages/TeacherDashboard";
import DirecteurDashboard from "@/pages/directeur/DirecteurDashboard";
import { ActivityCharts, useActivity } from "@/pages/admin/Activity";
import RegistrationStats from "@/pages/admin/RegistrationStats";
import { useNavigate } from "react-router-dom";

function Stat({ icon: Icon, label, value, tone = "green", testId }) {
  const tones = {
    green: "bg-[#E8F2EF] text-[#1F7A63]",
    yellow: "bg-[#FCF8E6] text-[#b38f00]",
    red: "bg-[#fbebeb] text-[#D93838]",
    slate: "bg-slate-100 text-slate-600",
  };
  return (
    <div className="ek-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-[#5C736A]">{label}</div>
          <div className="text-3xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>{value ?? "—"}</div>
        </div>
        <div className={`w-12 h-12 rounded-xl grid place-items-center ${tones[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const activity = useActivity(user.role === "admin");
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  const needsSchoolSetup =
    (user.role === "directeur" && !user.school_id) ||
    (user.role === "maitre" && user.is_independent && !user.school_id);

  useEffect(() => {
    if (needsSchoolSetup || user.role === "maitre" || user.role === "directeur") {
      setStats({});
      return;
    }
    const path =
      user.role === "admin" ? "/stats/admin" :
      user.role === "directeur" ? "/stats/directeur" : null;
    if (!path) {
      setStats({});
      return;
    }
    api.get(path).then(({ data }) => setStats(data)).catch((e) => setError(formatError(e)));
  }, [user, needsSchoolSetup]);

  if (needsSchoolSetup) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>
            Bienvenue, {user.name?.split(" ")[0]} 👋
          </h1>
          <p className="text-sm text-[#5C736A] mt-1">Une dernière étape avant de démarrer.</p>
        </div>
        <SchoolSetupCard />
      </div>
    );
  }

  if (!stats) return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skel h-28" />)}
    </div>
  );

  return (
    <div className="space-y-6">
      {user.role !== "maitre" && user.role !== "directeur" && (
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>
            Bonjour, {user.name?.split(" ")[0]} 👋
          </h1>
          <p className="text-sm text-[#5C736A] mt-1">Voici votre tableau de bord du jour.</p>
        </div>
      )}

      {error && <div className="ek-card p-4 text-red-600 text-sm">{error}</div>}

      {user.role === "admin" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Stat icon={Building2} label="Écoles" value={stats.ecoles} testId="stat-ecoles" />
            <Stat icon={Briefcase} label="Directeurs" value={stats.directeurs} testId="stat-directeurs" />
            <Stat icon={Users} label="Enseignants" value={stats.maitres} tone="slate" testId="stat-maitres" />
            <Stat icon={UserCircle} label="Élèves" value={stats.eleves} tone="yellow" testId="stat-eleves" />
            <Stat icon={ClipboardList} label="Comptes actifs" value={activity.d?.comptes_actifs} tone="slate" testId="stat-actifs" />
          </div>
          {activity.d && <ActivityCharts d={activity.d} compact />}
          <div>
            <h2 className="text-lg font-bold mb-3" style={{ fontFamily: "Outfit" }}>Inscriptions</h2>
            <RegistrationStats compact onOpen={(id) => navigate(`/app/registrations?id=${id}`)} />
          </div>
          <div className="ek-card p-5">
            <div className="font-semibold mb-3">Connexions récentes</div>
            <div className="space-y-2">
              {(stats.connexions_recentes || []).slice(0, 8).map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="font-medium">{c.username}</div>
                  <div className="text-[#5C736A] text-xs">{new Date(c.at).toLocaleString("fr-FR")}</div>
                </div>
              ))}
              {(!stats.connexions_recentes || stats.connexions_recentes.length === 0) && (
                <div className="text-sm text-[#5C736A]">Aucune connexion pour l&apos;instant.</div>
              )}
            </div>
          </div>
        </>
      )}

      {user.role === "directeur" && <DirecteurDashboard />}

      {user.role === "maitre" && <TeacherDashboard userName={user.name} />}

      {user.role === "inspecteur" && (
        <div className="ek-card p-6">
          <h2 className="text-lg font-semibold">Espace Inspecteur</h2>
          <p className="text-sm text-[#5C736A] mt-2">
          Vous avez un accès en <span className="font-semibold text-[#1F7A63]">lecture seule</span>. Consultez les écoles, classes, élèves et statistiques.
          </p>
        </div>
      )}
    </div>
  );
}
